import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Modal } from '@/components/ui'
import { currencySymbol, formatAmount } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { ClientReservation, MissingTariff, ReservationQuote } from '@/types/api'

import MissingPriceDialog from './MissingPriceDialog'
import { OrderHeader, TimeCell } from './PaymentDetailsModal'

interface Props {
  quote: ReservationQuote
  clientName: string
  onClose: () => void
}

/**
 * „Detaily platby“ rezervácie, ktorá ešte nie je uložená - len na čítanie.
 *
 * Cena je orientačná (`POST /client-reservations/quote/`, tým istým cenníkom
 * ako pri uložení). Položky, zľavy, platby a variabilný symbol vzniknú až
 * uložením; potom sa namiesto tohto okna otvorí `PaymentDetailsModal`.
 */
export default function PaymentQuoteModal({ quote, clientName, onClose }: Props) {
  const { t } = useTranslation()
  const symbol = currencySymbol()
  // Hlavička aj riadky čítajú z lekcie len začiatok a koniec.
  const lessons = quote.lessons.map((l) => ({ start: l.start, end: l.end }) as ClientReservation)
  const total = Number.parseFloat(quote.total)
  // Cenník smie meniť len správca (rovnako ako v Nastaveniach).
  const canEditPricing = Boolean(useAuth((s) => s.user)?.can_administrate)
  const [adding, setAdding] = useState<MissingTariff | null>(null)
  // Klient ešte nemusí byť vyplnený - nadpis potom bez mena.
  const title = t('payments.title')

  return (
    <Modal open onClose={onClose} size="lg" title={clientName ? `${clientName} — ${title}` : capitalize(title)}>
      <div className="space-y-2">
        <Alert variant="info">{t('payments.quoteInfo')}</Alert>
        {quote.unpriced ? <Alert variant="warning">{t('payments.unpricedHint', { count: quote.unpriced })}</Alert> : null}

        <OrderHeader
          paymentRef={null}
          lessons={lessons}
          total={total}
          paid={0}
          due={total}
          overpaid={0}
          settled={false}
          simple
          symbol={symbol}
        />

        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full border-collapse">
            <tbody>
              {quote.lessons.map((lesson, index) => (
                <tr key={`${lesson.start}-${index}`} className="border-b border-slate-200 last:border-0">
                  <td className="w-36 bg-slate-50 px-3 py-2 align-top">
                    <TimeCell reservation={lessons[index]} />
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700">{t('payments.basePrice')}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                    {lesson.price !== null ? (
                      <span className="font-medium tabular-nums">
                        {formatAmount(lesson.price)} {symbol}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-amber-700">{t('payments.unpriced')}</span>
                        {canEditPricing && lesson.tariff ? (
                          <button
                            type="button"
                            className="btn-secondary px-2 py-1 text-xs"
                            onClick={() => setAdding(lesson.tariff)}
                          >
                            {t('payments.addTariff')}
                          </button>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {adding ? <MissingPriceDialog tariff={adding} onClose={() => setAdding(null)} /> : null}
    </Modal>
  )
}

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1)
